import type { AgentLoadingState } from './useAgentLoadingState'
import type { ChatStatusMessageItem } from './types/ChatStatusMessageTypes'

export interface StatusItemsCtx {
    effectiveLoadingState: AgentLoadingState
    isPanelLoading: boolean
    isBrowseVisible: boolean
    streamDone: boolean
    groupSize: number
    isTroubleshootingQuery?: boolean
}

const buildBrowseChildren = (
    loadingState: AgentLoadingState,
    messages: { working: string; done: string }
): ChatStatusMessageItem[] => [
    {
        id: 'browse-child-working',
        message: messages.working,
        state: loadingState === 'agent_thinking' ? 'loading'
            : loadingState === 'responding' ? 'done'
            : 'pending',
    },
    {
        id: 'browse-child-done',
        message: messages.done,
        state: loadingState === 'responding' ? 'done' : 'pending',
    },
]

/** "1 matching solution found:" for one solution, "N matching solutions found:" for more: the number comes first, the text has two forms. */
const foundSolutionsMessage = (t: (k: string) => string, count: number): string =>
    `${count} ${t(`chat:statusMessage:troubleshooting:${count === 1 ? 'foundOne' : 'foundMoreThanOne'}`)}`

type Translate = (k: string) => string

const T = 'chat:statusMessage:troubleshooting'

// The pieces of a flow: a flow is these pieces in order
const thinkingItem = (message: string, isPanelLoading: boolean, loadingPhrases?: string[]): ChatStatusMessageItem => ({
    id: 'thinking',
    type: 'thinking',
    message,
    state: isPanelLoading ? 'loading' : 'done',
    ...(loadingPhrases && { loadingPhrases }),
})

const browseItem = (
    kind: 'Local' | 'Global',
    t: Translate,
    ctx: StatusItemsCtx,
    loadingPhrases?: string[]
): ChatStatusMessageItem[] => {
    if (!ctx.isBrowseVisible) return []
    const key = `${T}:browse${kind}Database`
    const type = kind === 'Local' ? 'browse-local-database' : 'browse-global-database'
    return [{
        id: type,
        type,
        message: t(`${key}:label`),
        state: ctx.isPanelLoading ? 'loading' : 'done',
        kind: 'group',
        ...(loadingPhrases && { loadingPhrases: ctx.isPanelLoading ? loadingPhrases : [] }),
        children: buildBrowseChildren(ctx.effectiveLoadingState, {
            working: t(`${key}:working`),
            done: t(`${key}:done`),
        }),
    }]
}

const solutionItem = (t: Translate, ctx: StatusItemsCtx): ChatStatusMessageItem[] =>
    ctx.isPanelLoading ? [] : [{
        id: ctx.streamDone ? 'solution-found--done' : 'solution-found--loading',
        type: 'solution-found',
        message: foundSolutionsMessage(t, ctx.groupSize),
        state: ctx.streamDone ? 'done' : 'loading',
    }]

export const buildLocalSearchStatusItems = (t: Translate, ctx: StatusItemsCtx): ChatStatusMessageItem[] => {
    const { isPanelLoading, isBrowseVisible, isTroubleshootingQuery = true } = ctx
    const thinking = thinkingItem(
        !isPanelLoading ? t(`${T}:thinking:done`)
            : isBrowseVisible ? t(`${T}:thinking:label`)
            : t(`${T}:thinking:label`),
        isPanelLoading,
        []
    )
    if (!isTroubleshootingQuery) return [thinking]
    return [thinking, ...browseItem('Local', t, ctx), ...solutionItem(t, ctx)]
}

export const buildGlobalSearchStatusItems = (
    t: Translate,
    ctx: StatusItemsCtx & { timeStr: string }
): ChatStatusMessageItem[] => {
    const { isPanelLoading, timeStr } = ctx
    const phrases = [
        `${t('chat:ipnSearch:searchExpanded')} (${timeStr}).`,
        t(`${T}:browseGlobalDatabase:label`),
    ]
    return [
        thinkingItem(t(`${T}:thinking:${isPanelLoading ? 'label' : 'done'}`), isPanelLoading),
        ...browseItem('Global', t, ctx, phrases),
        ...solutionItem(t, ctx),
    ]
}

// The status of an assistant that has no steps of its own: "Thinking..." until the first content arrives
export const buildThinkingStatusItems = (t: Translate): ChatStatusMessageItem[] => [thinkingItem(t(`${T}:thinking:label`), true, [])]
